// Filename → likely-doc-type heuristics for the doc-type picker modal.
//
// Purely client-side and intentionally dumb: tokenize the filename, drop the
// noise words, and score every doc type by how many of its own words the
// filename hit. Good enough to float "Bank Statement" to the top for
// "Chase-bank-statement-Feb-2026.pdf" without pretending to be a classifier.

const STOP_WORDS = new Set([
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "scan",
  "scanned",
  "final",
  "copy",
  "file",
  "image",
  "sample",
  "signed",
  "executed",
  "draft",
  "new",
  "and",
  "the",
  "for",
]);

export const normalizeText = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const tokenizeFileName = (fileName) =>
  normalizeText(String(fileName || "").replace(/\.[a-z0-9]+$/i, ""))
    .split(" ")
    .filter(
      (word) => word.length > 2 && !STOP_WORDS.has(word) && !/^\d+$/.test(word),
    );

// `items` is the flat [{ group, type }] list. Returns at most `limit` entries,
// best match first, or [] when nothing scores well enough to be worth showing.
export const getSuggestedDocTypes = (fileNames, items, limit = 4) => {
  const names = (Array.isArray(fileNames) ? fileNames : [fileNames]).filter(
    Boolean,
  );
  if (!names.length || !items?.length) return [];

  const tokens = new Set();
  names.forEach((name) => tokenizeFileName(name).forEach((t) => tokens.add(t)));
  if (!tokens.size) return [];

  const scored = items
    .map((item) => {
      const typeWords = normalizeText(item.type).split(" ").filter(Boolean);
      const wordSet = new Set(typeWords);
      let score = 0;
      tokens.forEach((token) => {
        if (wordSet.has(token)) score += 2;
        else if (
          typeWords.some(
            (word) => word.startsWith(token) || token.startsWith(word),
          )
        ) {
          score += 1;
        }
      });
      return { ...item, score };
    })
    .filter((item) => item.score >= 2)
    .sort((a, b) => b.score - a.score || a.type.length - b.type.length);

  return scored.slice(0, limit);
};
